import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from './useWorkspace';

export interface LearningInsight {
  type: 'loss_reason' | 'bottleneck' | 'performance';
  title: string;
  value: string | number;
  description: string;
  trend?: 'up' | 'down' | 'neutral';
}

export function useLearningInsights() {
  const { workspace } = useWorkspace();

  return useQuery({
    queryKey: ['learning-insights', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Fetch loss events and stage changes
      const [lossEvents, allActions, deals] = await Promise.all([
        supabase
          .from('action_events')
          .select('outcome, meta, created_at')
          .eq('workspace_id', workspace.id)
          .in('outcome', ['not_interested', 'competitor', 'price', 'timing', 'no_budget', 'other'])
          .gte('created_at', thirtyDaysAgo.toISOString()),
        
        supabase
          .from('action_events')
          .select('action_type, created_at, lead_id')
          .eq('workspace_id', workspace.id)
          .gte('created_at', thirtyDaysAgo.toISOString()),

        supabase
          .from('deals')
          .select('stage, created_at, updated_at')
          .eq('workspace_id', workspace.id),
      ]);

      // Calculate loss reasons
      const lossReasons: Record<string, number> = {};
      lossEvents.data?.forEach(event => {
        if (event.outcome) {
          lossReasons[event.outcome] = (lossReasons[event.outcome] || 0) + 1;
        }
      });

      const topLossReasons = Object.entries(lossReasons)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);

      // Calculate stage distribution (bottleneck detection)
      const stageDistribution: Record<string, number> = {};
      deals.data?.forEach(deal => {
        if (deal.stage && deal.stage !== 'won' && deal.stage !== 'lost') {
          stageDistribution[deal.stage] = (stageDistribution[deal.stage] || 0) + 1;
        }
      });

      const bottleneckStage = Object.entries(stageDistribution)
        .sort((a, b) => b[1] - a[1])[0];

      // Calculate average follow-up delay
      const actionsByLead: Record<string, Date[]> = {};
      allActions.data?.forEach(action => {
        if (action.lead_id) {
          if (!actionsByLead[action.lead_id]) {
            actionsByLead[action.lead_id] = [];
          }
          actionsByLead[action.lead_id].push(new Date(action.created_at));
        }
      });

      let totalDelays = 0;
      let delayCount = 0;
      Object.values(actionsByLead).forEach(dates => {
        dates.sort((a, b) => a.getTime() - b.getTime());
        for (let i = 1; i < dates.length; i++) {
          const delay = (dates[i].getTime() - dates[i - 1].getTime()) / (1000 * 60 * 60 * 24);
          if (delay > 0 && delay < 30) {
            totalDelays += delay;
            delayCount++;
          }
        }
      });
      const avgFollowUpDelay = delayCount > 0 ? totalDelays / delayCount : 0;

      // Build insights
      const insights: LearningInsight[] = [];

      if (topLossReasons.length > 0) {
        const [reason, count] = topLossReasons[0];
        insights.push({
          type: 'loss_reason',
          title: 'Top Loss Reason',
          value: reason.replace('_', ' '),
          description: `${count} deals lost to "${reason.replace('_', ' ')}" in the last 30 days`,
          trend: 'neutral',
        });
      }

      if (bottleneckStage) {
        insights.push({
          type: 'bottleneck',
          title: 'Pipeline Bottleneck',
          value: bottleneckStage[0],
          description: `${bottleneckStage[1]} deals stuck in "${bottleneckStage[0]}" stage`,
          trend: 'down',
        });
      }

      insights.push({
        type: 'performance',
        title: 'Avg Follow-up Delay',
        value: `${avgFollowUpDelay.toFixed(1)} days`,
        description: avgFollowUpDelay > 3 
          ? 'Consider faster follow-ups to improve conversion'
          : 'Good response time - maintain this pace',
        trend: avgFollowUpDelay > 3 ? 'down' : 'up',
      });

      // Add recommendations
      const recommendations: string[] = [];
      
      if (topLossReasons.some(([r]) => r === 'price')) {
        recommendations.push('Price objections are common. Consider value-based selling training or package restructuring.');
      }
      
      if (avgFollowUpDelay > 3) {
        recommendations.push('Follow-ups taking too long. Use Focus Mode to complete priority actions daily.');
      }
      
      if (bottleneckStage && bottleneckStage[0] === 'proposal_sent') {
        recommendations.push('Deals stalling after proposals. Review proposal quality and follow-up timing.');
      }

      return {
        lossReasons: topLossReasons,
        stageDistribution,
        avgFollowUpDelay,
        insights,
        recommendations,
        totalActions: allActions.data?.length || 0,
        totalDeals: deals.data?.length || 0,
      };
    },
    enabled: !!workspace?.id,
  });
}
